// src/features/MeterAnalysis/MeterDetailPage.js
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Grid,
  useTheme,
  alpha,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { 
  ArrowBack, 
  TrendingUp, 
  ShowChart, 
  CalendarToday,
  Speed,
  WaterDrop,
  LocalGasStation,
  BarChart,
  Timeline,
  Compare,
  Insights
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { useMeterData, METER_CONFIG } from './hooks/useMeterData';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart as RechartsBarChart, 
  Bar, 
  Legend,
  AreaChart,
  Area
} from 'recharts';

// --- Компоненти графіків ---
const TrendChart = ({ data, type = 'line', height = 300, unit = '', showForecast = false }) => {
  const theme = useTheme();
  const { t } = useI18n();
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('meter_analysis_no_data')}
        </Typography>
      </Box>
    );
  }

  const chartData = data.map(item => ({
    date: new Date(item.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
    value: item.value,
    consumption: item.consumption,
    fullDate: item.date
  }));

  // Простий прогноз споживання
  const calculateForecast = (data, periods = 7) => {
    if (data.length < 2) return [];
    
    const consumptions = data.map(d => d.consumption).filter(c => c > 0);
    if (consumptions.length < 2) return [];
    
    const n = consumptions.length;
    const xSum = consumptions.reduce((sum, _, i) => sum + i, 0);
    const ySum = consumptions.reduce((sum, val) => sum + val, 0);
    const xySum = consumptions.reduce((sum, val, i) => sum + i * val, 0);
    const x2Sum = consumptions.reduce((sum, _, i) => sum + i * i, 0);
    
    const slope = (n * xySum - xSum * ySum) / (n * x2Sum - xSum * xSum);
    const intercept = (ySum - slope * xSum) / n;
    
    const forecast = [];
    for (let i = n; i < n + periods; i++) {
      forecast.push({
        date: `+${i - n + 1}d`,
        consumption: Math.max(0, slope * i + intercept),
        isForecast: true
      });
    }
    
    return forecast;
  };

  const forecastData = showForecast ? calculateForecast(data) : [];
  const combinedData = [...chartData, ...forecastData];

  return (
    <ResponsiveContainer width="100%" height={height}>
      {type === 'line' ? (
        <LineChart data={combinedData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value, name) => {
              if (name === 'value') return [`${value} ${unit}`, t('meter_analysis_reading')];
              if (name === 'consumption') return [`${value} ${unit}`, t('meter_analysis_consumption')];
              return [value, name];
            }}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                const item = payload[0].payload;
                if (item.isForecast) {
                  return `${t('analytics_forecast_label')}: ${label}`;
                }
                return new Date(item.fullDate).toLocaleDateString('es-ES');
              }
              return label;
            }}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke={theme.palette.primary.main}
            strokeWidth={3}
            dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, fill: theme.palette.primary.dark }}
            name={t('meter_analysis_reading')}
          />
          <Line 
            type="monotone" 
            dataKey="consumption" 
            stroke={theme.palette.warning.main}
            strokeWidth={2}
            dot={{ fill: theme.palette.warning.main, r: 3 }}
            activeDot={{ r: 5, fill: theme.palette.warning.dark }}
            name={t('meter_analysis_consumption')}
          />
          {showForecast && forecastData.length > 0 && (
            <Line 
              type="monotone" 
              dataKey="consumption" 
              stroke={theme.palette.success.main}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: theme.palette.success.main, r: 3 }}
              name={t('analytics_forecast_label')}
              data={forecastData}
            />
          )}
        </LineChart>
      ) : type === 'area' ? (
        <AreaChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value, name) => {
              if (name === 'value') return [`${value} ${unit}`, t('meter_analysis_reading')];
              if (name === 'consumption') return [`${value} ${unit}`, t('meter_analysis_consumption')];
              return [value, name];
            }}
          />
          <Area 
            type="monotone" 
            dataKey="consumption" 
            stroke={theme.palette.warning.main}
            fill={alpha(theme.palette.warning.main, 0.3)}
            strokeWidth={2}
            name={t('meter_analysis_consumption')}
          />
        </AreaChart>
      ) : (
        <RechartsBarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value, name) => {
              if (name === 'value') return [`${value} ${unit}`, t('meter_analysis_reading')];
              if (name === 'consumption') return [`${value} ${unit}`, t('meter_analysis_consumption')];
              return [value, name];
            }}
          />
          <Legend />
          <Bar 
            dataKey="consumption" 
            fill={theme.palette.warning.main}
            name={t('meter_analysis_consumption')}
            radius={[4, 4, 0, 0]}
          />
        </RechartsBarChart>
      )}
    </ResponsiveContainer>
  );
};

const ConsumptionDistributionChart = ({ data, height = 250, unit = '' }) => {
  const theme = useTheme();
  const { t } = useI18n();
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('meter_analysis_no_data')}
        </Typography>
      </Box>
    );
  }

  // Групуємо споживання по діапазонах
  const consumptionRanges = data.reduce((acc, item) => {
    if (item.consumption > 0) {
      const range = Math.floor(item.consumption / 100) * 100;
      const rangeKey = `${range}-${range + 99}`;
      
      if (!acc[rangeKey]) {
        acc[rangeKey] = { range: rangeKey, count: 0, total: 0 };
      }
      acc[rangeKey].count += 1;
      acc[rangeKey].total += item.consumption;
    }
    return acc;
  }, {});

  const chartData = Object.values(consumptionRanges)
    .sort((a, b) => parseInt(a.range) - parseInt(b.range))
    .slice(0, 10); // Топ-10 діапазонів

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
        <XAxis 
          dataKey="range" 
          tick={{ fontSize: 12 }}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip 
          formatter={(value, name) => {
            if (name === 'count') return [value, t('meter_analysis_days_count')];
            if (name === 'total') return [`${value} ${unit}`, t('meter_analysis_total_consumption')];
            return [value, name];
          }}
        />
        <Legend />
        <Bar 
          dataKey="count" 
          fill={theme.palette.info.main}
          name={t('meter_analysis_days_count')}
          radius={[4, 4, 0, 0]}
        />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};

const MonthlyConsumptionChart = ({ data, height = 250, unit = '' }) => {
  const theme = useTheme();
  const { t } = useI18n();
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('meter_analysis_no_data')}
        </Typography>
      </Box>
    );
  }

  // Групуємо дані по місяцях
  const monthlyData = data.reduce((acc, item) => {
    const date = new Date(item.date);
    const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
    const monthName = date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    
    if (!acc[monthKey]) {
      acc[monthKey] = { month: monthName, totalConsumption: 0, averageConsumption: 0, days: 0 };
    }
    acc[monthKey].totalConsumption += item.consumption;
    acc[monthKey].days += 1;
    acc[monthKey].averageConsumption = acc[monthKey].totalConsumption / acc[monthKey].days;
    
    return acc;
  }, {});

  const chartData = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
        <XAxis 
          dataKey="month" 
          tick={{ fontSize: 12 }}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip 
          formatter={(value, name) => {
            if (name === 'totalConsumption') return [`${value.toFixed(1)} ${unit}`, t('meter_analysis_total_consumption')];
            if (name === 'averageConsumption') return [`${value.toFixed(1)} ${unit}`, t('meter_analysis_avg_consumption')];
            return [value, name];
          }}
        />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="totalConsumption" 
          stroke={theme.palette.primary.main}
          strokeWidth={3}
          name={t('meter_analysis_total_consumption')}
          dot={{ fill: theme.palette.primary.main, r: 4 }}
        />
        <Line 
          type="monotone" 
          dataKey="averageConsumption" 
          stroke={theme.palette.success.main}
          strokeWidth={2}
          name={t('meter_analysis_avg_consumption')}
          dot={{ fill: theme.palette.success.main, r: 3 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// --- Компонент картки статистики ---
const StatCard = ({ title, value, unit, subtitle, color = "primary", icon: Icon }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].light, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette[color].main, 0.15)}`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${theme.palette[color].main} 0%, ${theme.palette[color].dark} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette[color].main, 0.3)}`
            }}
          >
            <Icon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette[color].dark }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        
        <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette[color].dark, mb: 1 }}>
          {value}
          {unit && (
            <Typography component="span" variant="h5" sx={{ fontWeight: 600, color: 'text.secondary', ml: 0.5 }}>
              {unit}
            </Typography>
          )}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default function MeterDetailPage() {
  const { t } = useI18n();
  const { meterKey } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [tabIndex, setTabIndex] = useState(0);
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState('all');

  const { selectedMeterData, loading, error } = useMeterData(meterKey);
  const meterConfig = METER_CONFIG[meterKey];

  // Фільтруємо дані за вибраним періодом
  const filteredData = useMemo(() => {
    if (!selectedMeterData?.data) return [];
    
    const now = new Date();
    let cutoffDate = new Date(0);
    
    switch (timeRange) {
      case '30days':
        cutoffDate = new Date(now.setDate(now.getDate() - 30));
        break;
      case '90days':
        cutoffDate = new Date(now.setDate(now.getDate() - 90));
        break;
      case 'year':
        cutoffDate = new Date(now.setFullYear(now.getFullYear() - 1));
        break;
      default:
        return selectedMeterData.data;
    }
    
    return selectedMeterData.data.filter(item => new Date(item.date) >= cutoffDate);
  }, [selectedMeterData, timeRange]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          {t('common_loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/meter-analysis')}>
          {t('common_back')}
        </Button>
      </Box>
    );
  }

  if (!meterConfig || !selectedMeterData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6" sx={{ mb: 2 }}>
          {t('meter_analysis_meter_not_found')}: {meterKey}
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/meter-analysis')}>
          {t('common_back')}
        </Button>
      </Box>
    );
  }

  const { data, statistics } = selectedMeterData;
  const IconComponent = meterConfig.type === 'water' ? WaterDrop : LocalGasStation;
  const meterColor = meterConfig.type === 'water' ? 'info' : 'warning';

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Заголовок та навігація */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate('/meter-analysis')}
          variant="outlined"
          sx={{ borderRadius: '8px' }}
        >
          {t('common_back')}
        </Button>
        
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 60,
            height: 60,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${theme.palette[meterColor].main} 0%, ${theme.palette[meterColor].dark} 100%)`,
            boxShadow: `0 8px 24px ${alpha(theme.palette[meterColor].main, 0.3)}`
          }}
        >
          <IconComponent sx={{ color: 'white', fontSize: 32 }} />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
            {meterConfig.meterName}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip 
              label={meterConfig.unit} 
              color={meterColor}
              sx={{ fontWeight: 600 }}
            />
            <Chip 
              label={meterConfig.type === 'water' ? t('meter_type_water') : t('meter_type_propane')}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Chip 
              label={`${statistics.daysWithData} ${t('meter_analysis_days_with_data')}`}
              color="success"
              variant="outlined"
            />
          </Stack>
        </Box>
      </Stack>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('meter_analysis_total_consumption')}
            value={statistics.totalConsumption.toLocaleString('es-ES')}
            unit={meterConfig.unit}
            subtitle={t('meter_stats_total_desc')}
            color={meterColor}
            icon={TrendingUp}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('meter_analysis_avg_consumption')}
            value={statistics.averageConsumption.toFixed(1)}
            unit={meterConfig.unit}
            subtitle={t('meter_stats_avg_desc')}
            color="info"
            icon={ShowChart}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('meter_analysis_max_consumption')}
            value={statistics.maxConsumption.toLocaleString('es-ES')}
            unit={meterConfig.unit}
            subtitle={t('meter_stats_max_desc')}
            color="warning"
            icon={Speed}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('meter_analysis_days_with_data')}
            value={statistics.daysWithData}
            unit={t('meter_stats_days_unit')}
            subtitle={t('meter_stats_days_desc')}
            color="success"
            icon={CalendarToday}
          />
        </Grid>
      </Grid>

      {/* Контроли фільтрів */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('analytics_period')}</InputLabel>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                label={t('analytics_period')}
              >
                <MenuItem value="all">{t('analytics_period_all')}</MenuItem>
                <MenuItem value="30days">{t('analytics_period_30days')}</MenuItem>
                <MenuItem value="90days">{t('analytics_period_90days')}</MenuItem>
                <MenuItem value="year">{t('analytics_period_year')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('analytics_chart_type')}</InputLabel>
              <Select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                label={t('analytics_chart_type')}
              >
                <MenuItem value="line">{t('chart_type_line')}</MenuItem>
                <MenuItem value="area">{t('chart_type_area')}</MenuItem>
                <MenuItem value="bar">{t('chart_type_bar')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Вкладки аналітики */}
      <Card sx={{ mb: 4 }}>
        <Tabs 
          value={tabIndex} 
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Timeline />} label={t('meter_analysis_trend_forecast')} />
          <Tab icon={<BarChart />} label={t('meter_analysis_distribution')} />
          <Tab icon={<Compare />} label={t('meter_analysis_monthly_trends')} />
          <Tab icon={<Insights />} label={t('meter_analysis_detailed_table')} />
        </Tabs>
      </Card>

      {/* Контент вкладок */}
      {tabIndex === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              {t('meter_analysis_trend_forecast')}
            </Typography>
            <TrendChart 
              data={filteredData} 
              type={chartType} 
              height={500}
              unit={meterConfig.unit}
              showForecast={true}
            />
          </CardContent>
        </Card>
      )}

      {tabIndex === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              {t('meter_analysis_distribution')}
            </Typography>
            <ConsumptionDistributionChart 
              data={filteredData} 
              height={500}
              unit={meterConfig.unit}
            />
          </CardContent>
        </Card>
      )}

      {tabIndex === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              {t('meter_analysis_monthly_trends')}
            </Typography>
            <MonthlyConsumptionChart 
              data={data} 
              height={500}
              unit={meterConfig.unit}
            />
          </CardContent>
        </Card>
      )}

      {tabIndex === 3 && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, pb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {t('meter_analysis_detailed_table')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('meter_analysis_chronology')}
              </Typography>
            </Box>
            
            <Divider />
            
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                      {t('meter_analysis_date')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                      {t('meter_analysis_reading')} ({meterConfig.unit})
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                      {t('meter_analysis_consumption')} ({meterConfig.unit})
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                      {t('meter_analysis_shift')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length > 0 ? (
                    filteredData.map((record, index) => (
                      <TableRow 
                        key={index} 
                        hover
                        sx={{ 
                          '&:last-child td': { borderBottom: 0 },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {new Date(record.date).toLocaleDateString('es-ES')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Chip 
                            label={record.value.toLocaleString('es-ES')}
                            color={meterColor}
                            variant="filled"
                            sx={{ fontWeight: 600, minWidth: 80 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Chip 
                            label={record.consumption.toLocaleString('es-ES')}
                            color="warning"
                            variant="outlined"
                            sx={{ fontWeight: 600, minWidth: 80 }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Chip 
                            label={record.shift} 
                            size="small" 
                            variant="outlined" 
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        <Typography variant="body1">
                          {t('meter_analysis_no_data')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}


